import { useEffect, useMemo, useState } from "react";
import { requestPresignedDownload } from "../api/assets";
import { isAbsoluteUrl, normalizeAssetKeys } from "../utils/assets";

const cache = new Map();
const EXPIRY_SKEW_MS = 30 * 1000;

const getCachedUrl = (key) => {
  const entry = cache.get(key);
  if (!entry) return "";
  const expiresAt = entry.expiresAt || 0;
  if (expiresAt && Date.now() + EXPIRY_SKEW_MS >= expiresAt) {
    cache.delete(key);
    return "";
  }
  return entry.url || "";
};

const mergeCacheIntoMap = (keys) => {
  const result = {};
  keys.forEach((key) => {
    if (!key) return;
    const cached = getCachedUrl(key);
    if (cached) result[key] = cached;
  });
  return result;
};

const useSignedAssetUrls = (keys) => {
  const normalizedKeys = useMemo(() => normalizeAssetKeys(keys), [keys]);
  const [signedUrls, setSignedUrls] = useState(() => mergeCacheIntoMap(normalizedKeys));
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => {
    if (!normalizedKeys.length) return;
    const fromCache = mergeCacheIntoMap(normalizedKeys);
    if (Object.keys(fromCache).length) {
      setSignedUrls((prev) => ({ ...fromCache, ...prev }));
    }
  }, [normalizedKeys]);

  useEffect(() => {
    if (!normalizedKeys.length) return;
    const missing = normalizedKeys.filter((key) => {
      if (!key) return false;
      if (isAbsoluteUrl(key)) return false;
      return !getCachedUrl(key);
    });
    if (!missing.length) return;

    let cancelled = false;
    const run = async () => {
      try {
        const data = await requestPresignedDownload(missing);
        if (cancelled) return;
        const urls = data?.urls || {};
        const expiresIn = Number(data?.expiresIn || 0);
        const expiresAt = expiresIn ? Date.now() + expiresIn * 1000 : 0;
        Object.entries(urls).forEach(([key, url]) => {
          if (url) cache.set(key, { url, expiresAt });
        });
        setSignedUrls((prev) => ({ ...prev, ...urls }));
      } catch (err) {
        if (!cancelled) {
          console.error("Unable to presign asset downloads", err);
        }
      }
    };
    run();

    return () => {
      cancelled = true;
    };
  }, [normalizedKeys, refreshToken]);

  useEffect(() => {
    if (!normalizedKeys.length) return;
    const now = Date.now();
    let nextExpiry = 0;
    normalizedKeys.forEach((key) => {
      if (!key || isAbsoluteUrl(key)) return;
      const entry = cache.get(key);
      if (!entry?.expiresAt) return;
      const candidate = entry.expiresAt - EXPIRY_SKEW_MS;
      if (candidate > now && (!nextExpiry || candidate < nextExpiry)) {
        nextExpiry = candidate;
      }
    });
    if (!nextExpiry) return;
    const delay = Math.max(0, nextExpiry - now);
    const timer = setTimeout(() => setRefreshToken((value) => value + 1), delay);
    return () => clearTimeout(timer);
  }, [normalizedKeys, signedUrls]);

  return useMemo(() => ({ ...mergeCacheIntoMap(normalizedKeys), ...signedUrls }), [normalizedKeys, signedUrls]);
};

export default useSignedAssetUrls;
